import React from 'react'
import PropTypes from 'prop-types'
import { omit } from 'lodash-es'
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme'
import styled from '@emotion/styled'
import { ThemeProvider } from '@emotion/react'
import { connect } from 'react-redux'
import ModalShell from './ModalShell'
import SessionExpired from '../error/SessionExpired'
import Retry from '../../../screens/error/Retry'
import RequestBlocked from '../error/RequestBlocked'
import RetryError from '../../../screens/error/RetryError'
import NotFound from '../../../screens/common/NotFound'
import FullPageSpinner from '../utils/FullPageSpinner'
import { screenSmall } from '../utils/screen-sizes'
import { contentWrapperCss } from './style'

const ModalDiv = styled.div`
  height: 100%;

  @media (max-width: ${screenSmall}) {
    padding: 0 1rem;
  }
`

class ModalPage extends React.Component {
  static propTypes = {
    isFetching: PropTypes.bool,
    isFetchUIBlocking: PropTypes.bool,
    noContent: PropTypes.bool,
    isRequestBlocked: PropTypes.bool || PropTypes.object,
    isSessionExpired: PropTypes.bool,
    loginUrl: PropTypes.string,
    retryHandler: PropTypes.func,
    isRetryFailed: PropTypes.bool,
    isNotFound: PropTypes.bool,
    children: PropTypes.node,
    hideLogo: PropTypes.bool,
  }

  // render this when there is no content to show behind a spinner
  renderModalShell() {
    const { isFetching, noContent } = this.props
    return isFetching && noContent && <ModalShell />
  }

  // render this when there is existing content to show behind a spinner
  // - if the fetch should block the UI in the first place
  renderBlockingSpinner() {
    const { isFetching, noContent, isFetchUIBlocking } = this.props
    return isFetching && !noContent && isFetchUIBlocking && <FullPageSpinner oldFlowModal />
  }

  renderRetry() {
    const { retryHandler, isRetryFailed } = this.props
    if (!retryHandler) {
      return null
    }
    // needs improvement: thread in props passed down from a fetch-initiating, to make a flow fully reusable. tracked in #132.
    return isRetryFailed ? <RetryError {...this.props} /> : <Retry {...this.props} />
  }

  isRenderChildren() {
    const { noContent, retryHandler, isRequestBlocked } = this.props
    return !noContent && !retryHandler && !isRequestBlocked
  }

  renderChildren() {
    return this.isRenderChildren() ? (
      <div css={contentWrapperCss}>
        <div id="modalPage">
          {this.renderBlockingSpinner()}
          {this.props.children}
        </div>
      </div>
    ) : (
      <>
        {this.renderBlockingSpinner()}
        {this.renderModalShell()}
        {this.renderRetry()}
      </>
    )
  }

  renderContent() {
    const rest = omit(this.props, [
      'isFetching',
      'sessionExpired',
      'loginUrl',
      'children',
      'dispatch',
      'onDone',
    ])
    const { isSessionExpired, isRequestBlocked, loginUrl, isNotFound } = this.props
    const isRenderChildren = !(isSessionExpired || isNotFound || isRequestBlocked)

    return (
      <ThemeProvider theme={PAYPAL_THEME}>
        <ModalDiv {...rest}>
          {isRenderChildren && this.renderChildren()}
          {isSessionExpired && <SessionExpired loginUrl={loginUrl} />}
          {isRequestBlocked && (
            <RequestBlocked actionType={isRequestBlocked.actionType} {...this.props} />
          )}
          {isNotFound && <NotFound />}
        </ModalDiv>
      </ThemeProvider>
    )
  }

  render() {
    return this.renderContent()
  }
}

const stateProps = (state, ownProps) => {
  let { isFetching, isFetchUIBlocking, noContent, fetchRetry } = ownProps.fetchingState
    ? ownProps.fetchingState
    : state.card || {}

  const { retryHandler, isRetryFailed } = fetchRetry || {}

  // TODO To handle session expired
  const { isSessionExpired, isRequestBlocked, loginUrl, isNotFound, showLoadingSpinner } =
    state.common || {}

  if (showLoadingSpinner) {
    isFetching = true
    noContent = true
  }

  return {
    isFetching,
    isFetchUIBlocking,
    noContent,
    retryHandler,
    isRetryFailed,
    isSessionExpired,
    isRequestBlocked,
    isNotFound,
    loginUrl,
  }
}

export default connect(stateProps)(ModalPage)
